import { useEffect, useState } from 'react';

/**
 * Bridges asynchronous failures (Firestore snapshot listeners, timers, event
 * handlers) into React's render-time error flow so a {@link JcErrorBoundary} can
 * catch them. Error boundaries only catch errors *thrown during render*, never
 * those raised inside an async callback — so async code calls
 * {@link reportAsyncError}, and a component mounted under the boundary
 * ({@link useThrowReportedErrors}) re-throws it on its next render.
 */

type JcAsyncErrorListener = (error: Error) => void;

const listeners = new Set<JcAsyncErrorListener>();

/**
 * Surfaces an async error to the nearest {@link JcAsyncErrorBoundary}. Safe to
 * call from any callback. A no-op if no boundary is mounted (the error is still
 * the caller's to log).
 */
export const reportAsyncError = (error: Error): void => {
    listeners.forEach((listener) => listener(error));
};

/** Subscribes to reported async errors; returns an unsubscribe function. */
const subscribeToAsyncErrors = (listener: JcAsyncErrorListener): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

/**
 * Re-throws any {@link reportAsyncError} during render, letting a surrounding
 * error boundary catch it. Call this from a component mounted *inside* the
 * boundary (see {@link JcAsyncErrorBoundary}).
 */
export const useThrowReportedErrors = (): void => {
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => subscribeToAsyncErrors(setError), []);

    if (error) throw error;
};
